import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { PriceSampleSource, notePriceSample } from 'src/app/shared/pricing/price-summary.service';
import { SecurityService } from 'src/services/security/security.service';
import { pickPriceSample, samplePriceAccount, samplePriceCatalogue, samplePriceLimits } from './price-summary-demo-backend';

/**
 * Stands in for the three billing reads of the create forms' price summary. Data lives in
 * {@link ./price-summary-demo-backend.ts}; this file is the wiring. Self-cancelling, like the other layers:
 * 1. It acts only on the create forms (`/create/…`); the same addresses on any other page (Plans & billing has its
 *    own layer) pass through untouched.
 * 2. The real request always goes out; only a 404 ("no such endpoint") is answered from samples. Status 0, 401,
 *    403 and 5xx reach the summary as they are. So it moves to the real backend endpoint by endpoint.
 */

/** `/create/dubbing` and the other create forms. */
export function onCreateRoute(routerUrl: string): boolean {
  return /^\/create\/[^/?#]+/.test(routerUrl.split(/[?#]/)[0]);
}

/** Which of the three reads a request is; `null` = not ours. Exported for the spec. */
export function classifyPriceRequest(req: HttpRequest<unknown>): { source: PriceSampleSource; orgId: string } | null {
  if (req.method !== 'GET') return null;
  let path: string;
  try {
    path = new URL(req.url, 'http://x').pathname;
  } catch {
    path = req.url.split('?')[0];
  }
  if (/\/billing\/catalogue$/.test(path)) return { source: 'prices', orgId: '' };
  const m = path.match(/\/billing\/([^/]+)\/(account|limits)$/);
  if (!m) return null;
  return { source: m[2] === 'account' ? 'credit' : 'limits', orgId: m[1] };
}

export const priceSummaryDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const kind = classifyPriceRequest(req);
  if (!kind) return next(req);

  // inject() only works here, in the injection context.
  const router = inject(Router);
  const url = router.getCurrentNavigation()?.finalUrl?.toString() ?? router.url;
  if (!onCreateRoute(url)) return next(req);
  const sample = pickPriceSample(url);
  const viewerId = String(inject(SecurityService).securityObject?.userId ?? '');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // 404 = no such endpoint yet; anything else is a real failure the summary must see.
      if (error.status !== 404) return throwError(() => error);
      notePriceSample(kind.source);
      const body = kind.source === 'prices' ? samplePriceCatalogue()
        : kind.source === 'credit' ? samplePriceAccount(kind.orgId, viewerId, sample)
        : samplePriceLimits(sample);
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }),
  );
};
